import { Alert, Descriptions, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { rejectReturn, type StockTransfer } from './stockApi'

interface ReturnRejectModalProps {
  transfer: StockTransfer | null
  onClose: () => void
}

interface FormValues {
  note: string
}

/** Bosh ombor qaytarishni rad etadi — mol jo'natgan filialga qaytadi (ADR-043). */
export function ReturnRejectModal({ transfer, onClose }: ReturnRejectModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  const submit = useMutation({
    mutationFn: (values: FormValues) => rejectReturn(transfer?.id ?? '', values.note.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      onClose()
    },
  })

  const close = () => {
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open={transfer !== null}
      title={t('stockTransfer.returnReject.title')}
      okText={t('stockTransfer.returnReject.submit')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values))}
      confirmLoading={submit.isPending}
      destroyOnHidden
      width={560}
    >
      <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('stockTransfer.returnReject.note')} />
      {submit.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(submit.error, t)} />
      ) : null}
      {transfer ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('stockTransfer.field.item')}>{`${transfer.itemName} · ${transfer.sku}`}</Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.route')}>{`${transfer.fromNodeName} → ${transfer.toNodeName}`}</Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.sent')}>{`${transfer.quantitySent} ${transfer.unit}`}</Descriptions.Item>
          <Descriptions.Item label={t('stock.field.returnReason')}>{transfer.returnReason}</Descriptions.Item>
        </Descriptions>
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={submit.isPending}>
        <Form.Item
          name="note"
          label={t('stockTransfer.returnReject.reason')}
          rules={[
            { required: true, whitespace: true, message: t('stockTransfer.returnReject.reasonRequired') },
            { max: 500, message: t('stock.validation.reasonLength') },
          ]}
        >
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
